import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  Paper,
  FormControlLabel,
  Checkbox,
  MenuItem,
} from '@mui/material';
import { EventLookup } from '../types';
import { useTranslation } from '../i18n/TranslationContext';
import { signedMoney, supplementForOption, supplementSuffix } from '../utils/supplements';

export interface GroupParticipant {
  first_name: string;
  last_name: string;
  email: string;
  fiscal_code: string;
  lookups?: Record<string, string | number | boolean>;
}

interface Props {
  open: boolean;
  groupSize: number;
  unitAmount: number;
  /** Importo del referente (quota se partecipa + sue variazioni, mai sotto zero) */
  referenteAmount?: (participates: boolean) => number;
  /** Importo di un partecipante (quota + variazioni della sua scheda, mai sotto zero) */
  participantAmount?: (lookups: Record<string, any> | undefined) => number;
  priceName?: string;
  referenteEmail?: string;
  participantLookups?: EventLookup[];
  initial?: GroupParticipant[];
  initialReferenteParticipates?: boolean;
  onClose: () => void;
  onConfirm: (participants: GroupParticipant[], referenteParticipates: boolean) => void;
}

const emptyParticipant = (): GroupParticipant => ({
  first_name: '',
  last_name: '',
  email: '',
  fiscal_code: '',
});

const GroupParticipantsDialog: React.FC<Props> = ({ open, groupSize, unitAmount, referenteAmount, participantAmount, priceName, referenteEmail, participantLookups = [], initial, initialReferenteParticipates, onClose, onConfirm }) => {
  const { t, fmtMoney } = useTranslation();
  const [refPart, setRefPart] = useState(true);
  // group_size = quante persone si iscrivono; il checkbox decide solo se il
  // referente è una di loro. Spunta off → tutte le N schede sono da compilare.
  const others = Math.max(0, refPart ? groupSize - 1 : groupSize);
  const startIdx = refPart ? 2 : 1;
  const personsCount = groupSize;
  const [participants, setParticipants] = useState<GroupParticipant[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setRefPart(initialReferenteParticipates !== false);
      setErrors([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) {
      setParticipants(prev => Array.from({ length: others }, (_, i) => prev[i] ?? initial?.[i] ?? emptyParticipant()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, others]);

  const setField = (index: number, field: keyof GroupParticipant, value: string) => {
    setParticipants(prev => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  // I valori dei campi custom viaggiano keyed per lookup_id, come li legge il BE
  const setLookup = (index: number, lookupId: number, value: string | boolean) => {
    setParticipants(prev => prev.map((p, i) => (i === index ? { ...p, lookups: { ...p.lookups, [lookupId]: value } } : p)));
  };

  const normEmail = (e: string) => e.trim().toLowerCase();

  // Errore live sull'email del partecipante i: coincide col referente o con un
  // partecipante precedente — feedback immediato durante la compilazione
  const liveEmailError = (i: number): string => {
    const e = normEmail(participants[i]?.email ?? '');
    if (!e) return '';
    if (referenteEmail && e === normEmail(referenteEmail)) {
      return t('gruppo.email_referente', 'Email già usata dal referente: ogni partecipante deve avere la propria');
    }
    for (let j = 0; j < i; j++) {
      if (normEmail(participants[j]?.email ?? '') === e) {
        return t('gruppo.email_duplicata', 'Email già usata dal partecipante {{n}}', { n: j + startIdx });
      }
    }
    return '';
  };

  const liveCfError = (i: number): string => {
    const cf = (participants[i]?.fiscal_code ?? '').trim().toUpperCase();
    if (!cf) return '';
    for (let j = 0; j < i; j++) {
      if ((participants[j]?.fiscal_code ?? '').trim().toUpperCase() === cf) {
        return t('gruppo.cf_duplicato', 'Codice fiscale già usato dal partecipante {{n}}', { n: j + startIdx });
      }
    }
    return '';
  };

  const validate = (): boolean => {
    const errs: string[] = [];
    participants.forEach((p, i) => {
      const n = i + startIdx;
      if (!p.first_name.trim() || !p.last_name.trim()) errs.push(t('gruppo.err_nome', 'Partecipante {{n}}: nome e cognome sono obbligatori', { n }));
      if (!p.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email)) errs.push(t('gruppo.err_email', 'Partecipante {{n}}: email non valida', { n }));
      const cf = p.fiscal_code.trim().toUpperCase();
      if (!cf) errs.push(t('gruppo.err_cf_req', 'Partecipante {{n}}: il codice fiscale è obbligatorio', { n }));
      else if (!/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/.test(cf)) errs.push(t('gruppo.err_cf', 'Partecipante {{n}}: codice fiscale non valido', { n }));
      const emailErr = liveEmailError(i);
      if (emailErr) errs.push(t('gruppo.err_prefisso', 'Partecipante {{n}}: {{msg}}', { n, msg: emailErr.toLowerCase() }));
      const cfErr = liveCfError(i);
      if (cfErr) errs.push(t('gruppo.err_prefisso', 'Partecipante {{n}}: {{msg}}', { n, msg: cfErr.toLowerCase() }));
      participantLookups.forEach(pl => {
        if (pl.required !== 1) return;
        const v = p.lookups?.[pl.lookup_id];
        if (v === undefined || v === '' || v === false) errs.push(t('gruppo.err_campo', 'Partecipante {{n}}: il campo "{{field}}" è obbligatorio', { n, field: pl.lookup.name }));
      });
    });
    setErrors(errs);
    return errs.length === 0;
  };

  const handleConfirm = () => {
    if (validate()) onConfirm(participants.map(p => ({
      ...p,
      first_name: p.first_name.trim(),
      last_name: p.last_name.trim(),
      email: p.email.trim(),
      fiscal_code: p.fiscal_code.trim(),
    })), refPart);
  };

  const total = Math.round((
    (referenteAmount ? referenteAmount(refPart) : (refPart ? unitAmount : 0))
    + participants.reduce((acc, p) => acc + (participantAmount ? participantAmount(p.lookups) : unitAmount), 0)
  ) * 100) / 100;
  const supplementsTotal = Math.round((total - unitAmount * personsCount) * 100) / 100;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('gruppo.titolo', 'Iscrizione di gruppo')}
        <Typography variant="body2" color="text.secondary">
          {t('gruppo.sottotitolo', 'Un unico pagamento per tutte le persone iscritte. I dati del referente sono quelli del modulo principale.')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <FormControlLabel
          sx={{ mb: 1 }}
          control={<Checkbox checked={refPart} onChange={e => setRefPart(e.target.checked)} />}
          label={t('gruppo.checkbox_referente', 'Iscrivimi come partecipante al corso (referente incluso nel gruppo)')}
        />
        <Alert severity="info" sx={{ mb: 2 }}>
          {refPart
            ? (others === 1
              ? t('gruppo.alert_incluso_1', 'Tu sei incluso tra le {{n}} persone: inserisci i dati dell\'altro partecipante.', { n: groupSize })
              : t('gruppo.alert_incluso', 'Tu sei incluso tra le {{n}} persone: inserisci i dati degli altri {{m}} partecipanti.', { n: groupSize, m: others }))
            : (others === 1
              ? t('gruppo.alert_escluso_1', 'Stai iscrivendo 1 persona senza partecipare: compila la sua scheda. Sarai il referente per pagamento e comunicazioni.')
              : t('gruppo.alert_escluso', 'Stai iscrivendo {{m}} persone senza partecipare: compila una scheda per ciascuna. Sarai il referente per pagamento e comunicazioni.', { m: others }))}
        </Alert>

        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.map((e, i) => <div key={i}>{e}</div>)}
          </Alert>
        )}

        {participants.map((p, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              {t('gruppo.partecipante', 'Partecipante {{n}}', { n: i + startIdx })}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField fullWidth required label={t('form.nome', 'Nome')} value={p.first_name}
                onChange={e => setField(i, 'first_name', e.target.value)} />
              <TextField fullWidth required label={t('form.cognome', 'Cognome')} value={p.last_name}
                onChange={e => setField(i, 'last_name', e.target.value)} />
            </Box>
            <TextField fullWidth required type="email" label={t('form.email', 'Email')} value={p.email}
              error={!!liveEmailError(i)}
              helperText={liveEmailError(i) || undefined}
              onChange={e => setField(i, 'email', e.target.value)} sx={{ mb: 2 }} />
            <TextField fullWidth required label={t('form.cf', 'Codice Fiscale')} value={p.fiscal_code}
              error={!!liveCfError(i)}
              helperText={liveCfError(i) || undefined}
              inputProps={{ maxLength: 16, style: { textTransform: 'uppercase' } }}
              onChange={e => setField(i, 'fiscal_code', e.target.value.toUpperCase())} />
            {participantLookups
              .slice()
              .sort((a, b) => a.order - b.order)
              .map(pl => {
                const lk = pl.lookup;
                const val = p.lookups?.[pl.lookup_id];
                if (lk.type === 'checkbox') {
                  return (
                    <FormControlLabel key={pl.id} sx={{ mt: 1, display: 'flex' }}
                      control={<Checkbox checked={val === true} onChange={e => setLookup(i, pl.lookup_id, e.target.checked)} />}
                      label={[lk.name, supplementSuffix(supplementForOption(pl, null), t, fmtMoney)].filter(Boolean).join(' ')
                        + (pl.required === 1 ? ' *' : '')} />
                  );
                }
                if (lk.type === 'select' || lk.type === 'radio') {
                  return (
                    <TextField key={pl.id} select fullWidth sx={{ mt: 2 }}
                      required={pl.required === 1} label={lk.name}
                      helperText={pl.description || undefined}
                      value={val ?? ''}
                      onChange={e => setLookup(i, pl.lookup_id, e.target.value)}>
                      {lk.options.map(o => (
                        <MenuItem key={o.id} value={String(o.id)}>
                          {o.option} {supplementSuffix(supplementForOption(pl, o.id), t, fmtMoney)}
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                }
                return (
                  <TextField key={pl.id} fullWidth sx={{ mt: 2 }}
                    required={pl.required === 1} label={lk.name}
                    helperText={pl.description || undefined}
                    multiline={lk.type === 'textarea'}
                    minRows={lk.type === 'textarea' ? 3 : undefined}
                    type={lk.type === 'email' ? 'email' : lk.type === 'number' ? 'number' : lk.type === 'date' ? 'date' : 'text'}
                    InputLabelProps={lk.type === 'date' ? { shrink: true } : undefined}
                    value={val ?? ''}
                    onChange={e => setLookup(i, pl.lookup_id, e.target.value)} />
                );
              })}
          </Paper>
        ))}

        <Box sx={{ p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              {priceName ?? t('gruppo.iscrizione', 'Iscrizione')} × {personsCount === 1 ? t('gruppo.una_persona', '1 persona') : t('gruppo.n_persone', '{{n}} persone', { n: personsCount })}{refPart ? '' : ` ${t('gruppo.referente_escluso', '(referente escluso)')}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('gruppo.cadauno', '{{amount}} cad.', { amount: fmtMoney(unitAmount) })}
            </Typography>
          </Box>
          {supplementsTotal !== 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                {t('riepilogo.supplementi', 'Supplementi e riduzioni')}
              </Typography>
              <Typography variant="body2" color="text.secondary">{signedMoney(supplementsTotal, fmtMoney)}</Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Typography variant="subtitle1" fontWeight={600}>{t('gruppo.totale', 'Totale')}</Typography>
            <Typography variant="h6" fontWeight={600}>{fmtMoney(total)}</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t('gruppo.unica_soluzione', "Pagamento in un'unica soluzione, non rateizzabile.")}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }}>
        <Button variant="outlined" onClick={onClose}>{t('common.annulla', 'Annulla')}</Button>
        <Button variant="contained" onClick={handleConfirm}>{t('gruppo.continua', 'Continua al pagamento')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupParticipantsDialog;
